import React, { useState, useEffect } from 'react';
import {
  Hammer,
  Plus,
  Search,
  CheckCircle2,
  Clock,
  User,
  Calendar,
  Layers,
  FileText,
  AlertCircle,
  Truck,
  DollarSign,
  ShieldCheck,
  Check,
  X,
  Upload,
  Camera,
  MapPin,
  RefreshCw,
  TrendingUp,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext.tsx';
import {
  WorkOrderItem,
  WorkProgressLogItem,
  WorkVariationItem,
  MaterialRequirementItem,
  ContractorMaterialBillItem,
} from '../types/index.ts';

interface SiteWorkPageProps {
  onNavigate: (route: string, param?: string) => void;
}

export const SiteWorkPage: React.FC<SiteWorkPageProps> = ({ onNavigate }) => {
  const { currentUser } = useAuth();
  const currentRole = (currentUser?.role || 'ADMIN').toUpperCase();
  const isEstimatorOrFieldStaff = currentRole === 'ESTIMATOR' || currentRole === 'FIELD_STAFF';
  const isSupervisorOrAdmin = currentRole === 'SUPERVISOR' || currentRole === 'ADMIN' || currentRole === 'MANAGEMENT';

  const [activeTab, setActiveTab] = useState<'PROGRESS' | 'MATERIALS' | 'VARIATIONS'>('PROGRESS');
  const [workOrders, setWorkOrders] = useState<WorkOrderItem[]>([]);
  const [selectedWoId, setSelectedWoId] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  // Tab 1: Progress Logs
  const [progressLogs, setProgressLogs] = useState<WorkProgressLogItem[]>([]);
  const [showProgressModal, setShowProgressModal] = useState(false);
  const [progressSubmitting, setProgressSubmitting] = useState(false);
  const [progressForm, setProgressForm] = useState({
    stage: '50%',
    percent: 50,
    description: '',
    materialUsage: '',
    remainingMaterial: '',
    photoUrl: '',
    latitude: '24.8214',
    longitude: '67.0321',
  });

  // Tab 2: Material Requirements & Contractor Bills
  const [materialReqs, setMaterialReqs] = useState<MaterialRequirementItem[]>([]);
  const [contractorBills, setContractorBills] = useState<ContractorMaterialBillItem[]>([]);
  const [showReqModal, setShowReqModal] = useState(false);
  const [showBillModal, setShowBillModal] = useState(false);
  const [showApproveReqModal, setShowApproveReqModal] = useState<MaterialRequirementItem | null>(null);
  const [showUsageModal, setShowUsageModal] = useState<MaterialRequirementItem | null>(null);
  const [usageQtyInput, setUsageQtyInput] = useState('');
  const [usageNotesInput, setUsageNotesInput] = useState('');
  const [approveQtyInput, setApproveQtyInput] = useState('');
  const [reqForm, setReqForm] = useState({
    materialCode: 'MAT-001',
    materialName: 'Porcelain Floor Tiles 600x600',
    unit: 'Sft',
    calculatedQuantity: '100',
    wastageQuantity: '10',
    requestedQuantity: '110',
    notes: '',
  });
  const [billForm, setBillForm] = useState({
    contractorName: 'Al-Madina Contractors',
    materialId: 1,
    quantity: '10',
    amountPaid: '15000',
    supplierName: 'New Karachi Timber & Tile Market',
    invoiceOrBillNumber: 'INV-78912',
    receiptPhotoUrl: 'https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?w=800&auto=format&fit=crop&q=80',
    paymentSource: 'ACCOUNTS_ADVANCE',
    remarks: 'Cash purchase for site execution',
  });

  // Tab 3: Work Variations
  const [variations, setVariations] = useState<WorkVariationItem[]>([]);
  const [showVariationModal, setShowVariationModal] = useState(false);
  const [variationSubmitting, setVariationSubmitting] = useState(false);
  const [variationForm, setVariationForm] = useState({
    title: '',
    variationType: 'ADDITIONAL_MATERIAL' as const,
    description: '',
    originalQuantity: '8',
    additionalQuantity: '2',
    unit: 'Sheet',
    reason: '',
    measurements: '',
    photoUrl: '',
  });

  // Fetch Work Orders
  const fetchWorkOrders = async () => {
    try {
      setLoading(true);
      const res = await fetch('/api/work-orders');
      if (res.ok) {
        const data = await res.json();
        if (Array.isArray(data) && data.length > 0) {
          setWorkOrders(data);
          if (!selectedWoId) {
            setSelectedWoId(data[0].id);
          }
        }
      }
    } catch (err: any) {
      console.warn('Failed to load work orders:', err.message);
    } finally {
      setLoading(false);
    }
  };

  // Fetch Work Order Specific Data (Progress, Materials, Variations)
  const fetchWoDetails = async (woId: number) => {
    try {
      setRefreshing(true);
      const wo = workOrders.find((w) => w.id === woId);
      const ticketId = wo?.ticketId;

      // 1. Progress Logs
      const progRes = await fetch(`/api/work-orders/${woId}/progress`);
      if (progRes.ok) {
        const pData = await progRes.json();
        if (Array.isArray(pData)) setProgressLogs(pData);
      }

      // 2. Material Requirements
      const reqRes = await fetch(`/api/material-requirements?workOrderId=${woId}${ticketId ? `&ticketId=${ticketId}` : ''}`);
      if (reqRes.ok) {
        const rData = await reqRes.json();
        if (Array.isArray(rData)) setMaterialReqs(rData);
      }

      // 3. Variations
      const varRes = await fetch(`/api/variations?workOrderId=${woId}`);
      if (varRes.ok) {
        const vData = await varRes.json();
        if (Array.isArray(vData)) setVariations(vData);
      }

      // 4. Contractor Bills (only if not restricted)
      if (!isEstimatorOrFieldStaff) {
        const billRes = await fetch(`/api/contractor-bills?ticketId=${ticketId || 1}`, {
          headers: {
            'x-user-role': currentRole,
          },
        });
        if (billRes.ok) {
          const bData = await billRes.json();
          if (Array.isArray(bData)) setContractorBills(bData);
        } else if (billRes.status === 403) {
          setContractorBills([]);
        }
      }
    } catch (err: any) {
      console.warn('Error fetching WO details:', err.message);
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchWorkOrders();
  }, []);

  useEffect(() => {
    if (selectedWoId) {
      fetchWoDetails(selectedWoId);
    }
  }, [selectedWoId]);

  const selectedWo = workOrders.find((w) => w.id === selectedWoId);

  // Submit Progress Log
  const handleProgressSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedWoId) return;

    try {
      setProgressSubmitting(true);
      const res = await fetch(`/api/work-orders/${selectedWoId}/progress`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ticketId: selectedWo?.ticketId,
          progressStage: progressForm.stage,
          progressPercent: progressForm.percent,
          description: progressForm.description,
          materialUsage: progressForm.materialUsage,
          remainingMaterial: progressForm.remainingMaterial,
          photos: progressForm.photoUrl ? [progressForm.photoUrl] : [],
          latitude: progressForm.latitude,
          longitude: progressForm.longitude,
          staffName: currentUser?.name || 'Field Supervisor',
        }),
      });

      const data = await res.json();
      if (res.ok) {
        setToastMessage(`Progress recorded: Stage ${progressForm.stage} (${progressForm.percent}%)`);
        setShowProgressModal(false);
        setProgressForm({
          stage: '50%',
          percent: 50,
          description: '',
          materialUsage: '',
          remainingMaterial: '',
          photoUrl: '',
          latitude: '24.8214',
          longitude: '67.0321',
        });
        await fetchWorkOrders();
        await fetchWoDetails(selectedWoId);
      } else {
        setToastMessage(data.error || 'Failed to record progress');
      }
    } catch (err: any) {
      setToastMessage(err.message || 'Progress update error');
    } finally {
      setProgressSubmitting(false);
      setTimeout(() => setToastMessage(null), 5000);
    }
  };

  // Submit Material Requirement Request
  const handleReqSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedWoId) return;

    try {
      const res = await fetch('/api/material-requirements', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ticketId: selectedWo?.ticketId || 1,
          workOrderId: selectedWoId,
          materialCode: reqForm.materialCode,
          materialName: reqForm.materialName,
          unit: reqForm.unit,
          calculatedQuantity: reqForm.calculatedQuantity,
          wastageQuantity: reqForm.wastageQuantity,
          requestedQuantity: reqForm.requestedQuantity,
          notes: reqForm.notes,
        }),
      });

      const data = await res.json();
      if (res.ok) {
        setToastMessage('Material requirement submitted successfully!');
        setShowReqModal(false);
        await fetchWoDetails(selectedWoId);
      } else {
        setToastMessage(data.error || 'Failed to submit requirement');
      }
    } catch (err: any) {
      setToastMessage(err.message || 'Error creating material requirement');
    } finally {
      setTimeout(() => setToastMessage(null), 5000);
    }
  };

  // Approve Extra Material Requirement
  const handleApproveReq = async (id: number) => {
    try {
      const res = await fetch(`/api/material-requirements/${id}/approve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          approvedQuantity: approveQtyInput,
          approvedBy: currentUser?.name || 'Project Manager',
          notes: 'Approved via site execution portal',
        }),
      });

      if (res.ok) {
        setToastMessage(`Material requirement ${id} approved for ${approveQtyInput} units!`);
        setShowApproveReqModal(null);
        if (selectedWoId) await fetchWoDetails(selectedWoId);
      }
    } catch (err: any) {
      setToastMessage(err.message || 'Approval error');
    } finally {
      setTimeout(() => setToastMessage(null), 5000);
    }
  };

  // Goods Receipt on Site Verification (Internal Procurement)
  const handleReceiveSite = async (id: number) => {
    try {
      const res = await fetch(`/api/material-requirements/${id}/receive-site`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          notes: `Material goods receipt verified on site by ${currentUser?.name || 'Site Supervisor'}`,
        }),
      });
      if (res.ok) {
        setToastMessage('Goods receipt verified: Materials received on site!');
        if (selectedWoId) await fetchWoDetails(selectedWoId);
      }
    } catch (err: any) {
      setToastMessage(err.message || 'Receipt verification error');
    } finally {
      setTimeout(() => setToastMessage(null), 5000);
    }
  };

  // Update actual material usage & remaining quantities on site
  const handleUsageSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!showUsageModal) return;

    try {
      const res = await fetch(`/api/material-requirements/${showUsageModal.id}/usage`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          usedQuantity: usageQtyInput,
          notes: usageNotesInput || 'Updated material usage on site',
        }),
      });

      if (res.ok) {
        setToastMessage(`Material usage updated: ${usageQtyInput} ${showUsageModal.unit} consumed on site.`);
        setShowUsageModal(null);
        setUsageQtyInput('');
        setUsageNotesInput('');
        if (selectedWoId) await fetchWoDetails(selectedWoId);
      } else {
        const errData = await res.json();
        setToastMessage(errData.error || 'Failed to update material usage');
      }
    } catch (err: any) {
      setToastMessage(err.message || 'Material usage error');
    } finally {
      setTimeout(() => setToastMessage(null), 5000);
    }
  };

  // Submit Contractor Bill
  const handleBillSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedWoId) return;

    try {
      const res = await fetch('/api/contractor-bills', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-user-role': currentRole,
        },
        body: JSON.stringify({
          ticketId: selectedWo?.ticketId || 1,
          workOrderId: selectedWoId,
          ...billForm,
        }),
      });

      const data = await res.json();
      if (res.ok) {
        setToastMessage(`Contractor bill submitted: ${data.billNumber}`);
        setShowBillModal(false);
        await fetchWoDetails(selectedWoId);
      } else {
        setToastMessage(data.error || 'Failed to submit contractor bill');
      }
    } catch (err: any) {
      setToastMessage(err.message || 'Error submitting contractor bill');
    } finally {
      setTimeout(() => setToastMessage(null), 5000);
    }
  };

  // Supervisor Verify Contractor Bill
  const handleVerifyBill = async (id: number) => {
    try {
      const res = await fetch(`/api/contractor-bills/${id}/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          verifiedBy: currentUser?.name || 'Engr. Bilal Hashmi',
        }),
      });
      if (res.ok) {
        setToastMessage('Contractor bill verified on site by Supervisor!');
        if (selectedWoId) await fetchWoDetails(selectedWoId);
      }
    } catch (err: any) {
      setToastMessage(err.message || 'Verification error');
    } finally {
      setTimeout(() => setToastMessage(null), 5000);
    }
  };

  // Accounts Settle Contractor Bill
  const handleSettleBill = async (id: number) => {
    try {
      const res = await fetch(`/api/contractor-bills/${id}/settle`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          settlementStatus: 'SETTLED',
        }),
      });
      if (res.ok) {
        setToastMessage('Contractor bill settled and adjusted by Accounts!');
        if (selectedWoId) await fetchWoDetails(selectedWoId);
      }
    } catch (err: any) {
      setToastMessage(err.message || 'Settlement error');
    } finally {
      setTimeout(() => setToastMessage(null), 5000);
    }
  };

  // Submit Work Variation
  const handleVariationSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedWoId) return;

    try {
      setVariationSubmitting(true);
      const res = await fetch('/api/variations', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ticketId: selectedWo?.ticketId || 1,
          workOrderId: selectedWoId,
          title: variationForm.title,
          variationType: variationForm.variationType,
          description: variationForm.description,
          originalQuantity: variationForm.originalQuantity,
          additionalQuantity: variationForm.additionalQuantity,
          unit: variationForm.unit,
          reason: variationForm.reason,
          measurements: variationForm.measurements,
          photos: variationForm.photoUrl ? [variationForm.photoUrl] : [],
          requesterName: currentUser?.name || 'Field Engineer',
          requesterRole: currentRole,
        }),
      });

      const data = await res.json();
      if (res.ok) {
        setToastMessage(`Variation submitted: ${data.variationNumber} (+${variationForm.additionalQuantity} ${variationForm.unit})`);
        setShowVariationModal(false);
        setVariationForm({
          title: '',
          variationType: 'ADDITIONAL_MATERIAL',
          description: '',
          originalQuantity: '8',
          additionalQuantity: '2',
          unit: 'Sheet',
          reason: '',
          measurements: '',
          photoUrl: '',
        });
        await fetchWoDetails(selectedWoId);
      } else {
        setToastMessage(data.error || 'Failed to submit variation');
      }
    } catch (err: any) {
      setToastMessage(err.message || 'Variation error');
    } finally {
      setVariationSubmitting(false);
      setTimeout(() => setToastMessage(null), 5000);
    }
  };

  // Approve Variation
  const handleApproveVariation = async (id: number) => {
    try {
      const res = await fetch(`/api/variations/${id}/approve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          status: 'APPROVED',
          approvedBy: currentUser?.name || 'Management',
          remarks: 'Approved by authorized Management',
        }),
      });

      if (res.ok) {
        setToastMessage(`Variation ${id} approved! Original approved baseline preserved.`);
        if (selectedWoId) await fetchWoDetails(selectedWoId);
      }
    } catch (err: any) {
      setToastMessage(err.message || 'Approval error');
    } finally {
      setTimeout(() => setToastMessage(null), 5000);
    }
  };

  return (
    <div className="space-y-6">
      {/* Toast Notification */}
      {toastMessage && (
        <div className="fixed top-4 right-4 z-50 bg-[#0f2942] text-white text-xs px-4 py-3 rounded-lg shadow-xl flex items-center gap-2 border border-slate-700">
          <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />
          <span>{toastMessage}</span>
        </div>
      )}

      {/* Top Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-xl sm:text-2xl font-bold text-slate-900 tracking-tight">
              Site Execution &amp; Work Operations
            </h1>
            <span className="px-2 py-0.5 rounded text-[11px] font-bold bg-blue-100 text-blue-900 border border-blue-200">
              Phase C Complete
            </span>
          </div>
          <p className="text-xs sm:text-sm text-slate-500 mt-0.5">
            Full physical execution workflow: Surveys, Independent Material Quantities, Dual Fulfillment, Progress Logs, and Variations.
          </p>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => onNavigate('job-verification')}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-50 text-indigo-700 hover:bg-indigo-100 rounded-lg text-xs font-semibold border border-indigo-200 transition-colors"
          >
            <ShieldCheck className="w-3.5 h-3.5" />
            <span>Joint Verification →</span>
          </button>
          <button
            onClick={() => onNavigate('completion')}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-[#0f2942] hover:bg-[#183d5f] text-white rounded-lg text-xs font-semibold shadow-2xs transition-colors"
          >
            <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />
            <span>Certificates →</span>
          </button>
        </div>
      </div>

      {/* Work Order Selector Bar */}
      <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-2xs flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 w-full md:w-auto">
          <label className="text-xs font-bold text-slate-700 whitespace-nowrap">Select Active Work Order:</label>
          <select
            className="text-xs bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 font-medium text-slate-800 w-full sm:w-80 focus:outline-hidden focus:ring-1 focus:ring-blue-500"
            value={selectedWoId || ''}
            onChange={(e) => setSelectedWoId(Number(e.target.value))}
          >
            {workOrders.map((wo) => (
              <option key={wo.id} value={wo.id}>
                {wo.workOrderNumber} - {wo.branchName || `Branch ${wo.branchId}`} ({wo.status})
              </option>
            ))}
          </select>
        </div>

        {selectedWo && (
          <div className="flex items-center gap-2 flex-wrap text-xs">
            <span className="px-2.5 py-1 rounded-full font-bold bg-slate-100 text-slate-800 border border-slate-200">
              Stage: {selectedWo.progressStage || 'NOT_STARTED'} ({selectedWo.progressPercent || 0}%)
            </span>
            <span
              className={`px-2.5 py-1 rounded-full font-bold border ${
                selectedWo.status === 'COMPLETED' || selectedWo.status === 'VERIFIED'
                  ? 'bg-emerald-100 text-emerald-800 border-emerald-200'
                  : selectedWo.status === 'IN_PROGRESS'
                  ? 'bg-blue-100 text-blue-800 border-blue-200'
                  : selectedWo.status === 'ON_SITE'
                  ? 'bg-amber-100 text-amber-800 border-amber-200'
                  : 'bg-slate-100 text-slate-700 border-slate-200'
              }`}
            >
              Status: {selectedWo.status}
            </span>
            <button
              onClick={() => selectedWoId && fetchWoDetails(selectedWoId)}
              className="p-1.5 rounded-lg border border-slate-200 text-slate-500 hover:text-slate-800 hover:bg-slate-50 transition-colors"
              title="Refresh Work Order Data"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} />
            </button>
          </div>
        )}
      </div>

      {/* Tabs Navigation */}
      <div className="flex border-b border-slate-200 gap-2">
        <button
          onClick={() => setActiveTab('PROGRESS')}
          className={`flex items-center gap-2 px-4 py-2.5 text-xs font-bold border-b-2 transition-colors ${
            activeTab === 'PROGRESS'
              ? 'border-blue-600 text-blue-600'
              : 'border-transparent text-slate-500 hover:text-slate-700'
          }`}
        >
          <Clock className="w-4 h-4" />
          <span>Execution &amp; Progress Logs</span>
          <span className="px-1.5 py-0.2 bg-slate-100 text-slate-600 rounded text-[10px]">
            {progressLogs.length}
          </span>
        </button>

        <button
          onClick={() => setActiveTab('MATERIALS')}
          className={`flex items-center gap-2 px-4 py-2.5 text-xs font-bold border-b-2 transition-colors ${
            activeTab === 'MATERIALS'
              ? 'border-blue-600 text-blue-600'
              : 'border-transparent text-slate-500 hover:text-slate-700'
          }`}
        >
          <Truck className="w-4 h-4" />
          <span>Material Requirements &amp; Fulfillment</span>
          <span className="px-1.5 py-0.2 bg-slate-100 text-slate-600 rounded text-[10px]">
            {materialReqs.length}
          </span>
        </button>

        <button
          onClick={() => setActiveTab('VARIATIONS')}
          className={`flex items-center gap-2 px-4 py-2.5 text-xs font-bold border-b-2 transition-colors ${
            activeTab === 'VARIATIONS'
              ? 'border-blue-600 text-blue-600'
              : 'border-transparent text-slate-500 hover:text-slate-700'
          }`}
        >
          <TrendingUp className="w-4 h-4" />
          <span>Work Variations &amp; Scope Changes</span>
          <span className="px-1.5 py-0.2 bg-slate-100 text-slate-600 rounded text-[10px]">
            {variations.length}
          </span>
        </button>
      </div>

      {/* TAB 1: EXECUTION & PROGRESS LOGS */}
      {activeTab === 'PROGRESS' && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-sm font-bold text-slate-900">Live Field Progress Timeline</h2>
              <p className="text-xs text-slate-500">Record milestone progress: 25%, 50%, 75%, Completed with material consumption &amp; GPS</p>
            </div>
            <button
              onClick={() => setShowProgressModal(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-[#0f2942] hover:bg-[#183d5f] text-white rounded-lg text-xs font-semibold shadow-2xs transition-colors"
            >
              <Plus className="w-3.5 h-3.5 text-amber-400" />
              <span>Log Milestone Progress</span>
            </button>
          </div>

          {progressLogs.length === 0 ? (
            <div className="bg-white rounded-xl border border-slate-200 p-8 text-center text-slate-500">
              <Clock className="w-8 h-8 text-slate-300 mx-auto mb-2" />
              <p className="text-xs font-medium">No progress updates recorded yet for this work order.</p>
              <p className="text-[11px] text-slate-400 mt-1">Field staff can update progress via Mobile Jobs or supervisor log above.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {progressLogs.map((log) => (
                <div key={log.id} className="bg-white p-4 rounded-xl border border-slate-200 shadow-2xs space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="px-2 py-0.5 rounded font-bold text-xs bg-blue-50 text-blue-700 border border-blue-200">
                        {log.progressStage} ({log.progressPercent}%)
                      </span>
                      {log.isGpsVerified && (
                        <span className="flex items-center gap-1 text-[10px] font-bold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded border border-emerald-200">
                          <MapPin className="w-3 h-3 text-emerald-600" />
                          GPS Verified
                        </span>
                      )}
                    </div>
                    <span className="text-[11px] text-slate-400">{log.timestamp ? new Date(log.timestamp).toLocaleString() : ''}</span>
                  </div>

                  <p className="text-xs text-slate-800 font-medium">{log.description}</p>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs bg-slate-50 p-2.5 rounded-lg border border-slate-100">
                    <div>
                      <span className="text-slate-500 font-semibold">Material Usage: </span>
                      <span className="text-slate-700">{log.materialUsage || 'None specified'}</span>
                    </div>
                    <div>
                      <span className="text-slate-500 font-semibold">Remaining Material: </span>
                      <span className="text-slate-700">{log.remainingMaterial || 'None specified'}</span>
                    </div>
                    <div className="sm:col-span-2 text-[11px] text-slate-500">
                      Logged by: <span className="font-semibold text-slate-700">{log.staffName}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* TAB 2: MATERIAL REQUIREMENTS & DUAL FULFILLMENT */}
      {activeTab === 'MATERIALS' && (
        <div className="space-y-6">
          {/* Top Bar for Materials */}
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-sm font-bold text-slate-900">Preserved Independent Quantities</h2>
              <p className="text-xs text-slate-500">
                Calculated, Wastage, Required (= Calc + Waste), Requested, Variance, Approved, Fulfilled, Used, and Remaining
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowReqModal(true)}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-xs font-semibold shadow-2xs transition-colors"
              >
                <Plus className="w-3.5 h-3.5" />
                <span>New Material Request</span>
              </button>
              {!isEstimatorOrFieldStaff && (
                <button
                  onClick={() => setShowBillModal(true)}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-xs font-semibold shadow-2xs transition-colors"
                >
                  <DollarSign className="w-3.5 h-3.5" />
                  <span>Contractor Purchase Bill</span>
                </button>
              )}
            </div>
          </div>

          {/* Independent Quantities Table */}
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-2xs">
            <div className="overflow-x-auto">
              <table className="w-full text-xs text-left">
                <thead className="bg-slate-50 text-slate-700 border-b border-slate-200 font-bold">
                  <tr>
                    <th className="p-3">Material</th>
                    <th className="p-3">Calc. Qty</th>
                    <th className="p-3">Waste Qty</th>
                    <th className="p-3">Required Qty</th>
                    <th className="p-3">Requested Qty</th>
                    <th className="p-3">Variance</th>
                    <th className="p-3">Approved Qty</th>
                    <th className="p-3">Fulfilled Qty</th>
                    <th className="p-3">Used Qty</th>
                    <th className="p-3">Remaining</th>
                    <th className="p-3">Status</th>
                    <th className="p-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {materialReqs.map((req) => {
                    const varianceVal = Number(req.varianceQuantity || 0);
                    return (
                      <tr key={req.id} className="hover:bg-slate-50/50">
                        <td className="p-3 font-semibold text-slate-800">
                          {req.materialName || req.materialCode}
                          <div className="text-[10px] text-slate-400 font-normal">{req.unit}</div>
                        </td>
                        <td className="p-3 font-mono">{req.calculatedQuantity}</td>
                        <td className="p-3 font-mono text-amber-700">{req.wastageQuantity}</td>
                        <td className="p-3 font-mono font-bold text-slate-900">{req.requiredQuantity}</td>
                        <td className="p-3 font-mono text-blue-700 font-semibold">{req.requestedQuantity}</td>
                        <td className="p-3 font-mono">
                          <span
                            className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${
                              varianceVal > 0 ? 'bg-red-50 text-red-700 border border-red-200' : 'bg-slate-100 text-slate-600'
                            }`}
                          >
                            {varianceVal > 0 ? `+${req.varianceQuantity}` : req.varianceQuantity}
                          </span>
                        </td>
                        <td className="p-3 font-mono font-bold text-emerald-700">{req.approvedQuantity || 'Pending'}</td>
                        <td className="p-3 font-mono">{req.fulfilledQuantity}</td>
                        <td className="p-3 font-mono text-slate-600">{req.usedQuantity || '0.00'}</td>
                        <td className="p-3 font-mono font-bold text-indigo-700">{req.remainingQuantity || '0.00'}</td>
                        <td className="p-3">
                          <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-slate-100 text-slate-700">
                            {req.fulfillmentStatus}
                          </span>
                        </td>
                        <td className="p-3 text-right space-x-1">
                          {isSupervisorOrAdmin && Number(req.approvedQuantity || 0) < Number(req.requestedQuantity) && (
                            <button
                              onClick={() => {
                                setShowApproveReqModal(req);
                                setApproveQtyInput(String(req.requestedQuantity));
                              }}
                              className="px-2 py-1 bg-amber-500 hover:bg-amber-600 text-white rounded text-[10px] font-bold"
                              title="Approve Extra Quantity"
                            >
                              Approve Extra
                            </button>
                          )}
                          {req.fulfillmentStatus !== 'RECEIVED_ON_SITE' && (
                            <button
                              onClick={() => handleReceiveSite(req.id)}
                              className="px-2 py-1 bg-emerald-600 hover:bg-emerald-700 text-white rounded text-[10px] font-bold"
                              title="Verify site goods receipt"
                            >
                              Verify Receipt
                            </button>
                          )}
                          <button
                            onClick={() => {
                              setShowUsageModal(req);
                              setUsageQtyInput(String(req.usedQuantity || ''));
                              setUsageNotesInput('');
                            }}
                            className="px-2 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded text-[10px] font-bold"
                            title="Log physical material usage & update remaining"
                          >
                            Log Usage
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Dual Fulfillment: Contractor Direct Purchase Section */}
          <div className="space-y-3 pt-4 border-t border-slate-200">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-sm font-bold text-slate-900">Contractor Direct Cash Purchases</h3>
                <p className="text-xs text-slate-500">Contractor bills, site verification by supervisor, and settlement by accounts</p>
              </div>
            </div>

            {isEstimatorOrFieldStaff ? (
              <div className="bg-amber-50 border border-amber-200 p-4 rounded-xl text-xs text-amber-800 flex items-center gap-2">
                <AlertCircle className="w-4 h-4 text-amber-600 shrink-0" />
                <span>Security Notice: Contractor financial bills and purchase prices are restricted from Estimator and Field Staff roles.</span>
              </div>
            ) : contractorBills.length === 0 ? (
              <div className="bg-white rounded-xl border border-slate-200 p-6 text-center text-xs text-slate-500">
                No contractor direct purchase bills logged for this ticket.
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {contractorBills.map((bill) => (
                  <div key={bill.id} className="bg-white p-4 rounded-xl border border-slate-200 shadow-2xs space-y-2 text-xs">
                    <div className="flex items-center justify-between">
                      <span className="font-bold text-blue-900">{bill.billNumber}</span>
                      <span className="font-bold px-2 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200">
                        PKR {Number(bill.amountPaid).toLocaleString()}
                      </span>
                    </div>

                    <div className="text-slate-700">
                      Contractor: <span className="font-semibold">{bill.contractorName}</span>
                    </div>
                    <div className="text-slate-500 text-[11px]">
                      Supplier: {bill.supplierName} | Invoice: {bill.invoiceOrBillNumber}
                    </div>

                    <div className="flex items-center justify-between pt-2 border-t border-slate-100">
                      <div className="space-x-1">
                        <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">
                          {bill.verificationStatus}
                        </span>
                        <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">
                          {bill.settlementStatus}
                        </span>
                      </div>

                      <div className="space-x-1">
                        {bill.verificationStatus !== 'VERIFIED_BY_SUPERVISOR' && (
                          <button
                            onClick={() => handleVerifyBill(bill.id)}
                            className="px-2 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded text-[10px] font-bold"
                          >
                            Supervisor Verify
                          </button>
                        )}
                        {bill.settlementStatus !== 'SETTLED' && (
                          <button
                            onClick={() => handleSettleBill(bill.id)}
                            className="px-2 py-1 bg-emerald-600 hover:bg-emerald-700 text-white rounded text-[10px] font-bold"
                          >
                            Accounts Settle
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* TAB 3: WORK VARIATIONS */}
      {activeTab === 'VARIATIONS' && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-sm font-bold text-slate-900">Work Variations (Scope &amp; Material Changes)</h2>
              <p className="text-xs text-slate-500">
                Original approved quantities remain immutable. Additional quantities undergo formal approval.
              </p>
            </div>
            <button
              onClick={() => setShowVariationModal(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-[#0f2942] hover:bg-[#183d5f] text-white rounded-lg text-xs font-semibold shadow-2xs transition-colors"
            >
              <Plus className="w-3.5 h-3.5 text-amber-400" />
              <span>Submit Variation Request</span>
            </button>
          </div>

          {variations.length === 0 ? (
            <div className="bg-white rounded-xl border border-slate-200 p-8 text-center text-slate-500">
              <TrendingUp className="w-8 h-8 text-slate-300 mx-auto mb-2" />
              <p className="text-xs font-medium">No variations recorded for this work order.</p>
              <p className="text-[11px] text-slate-400 mt-1">If unforeseen site conditions require extra materials or scope, log a variation request.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {variations.map((v) => (
                <div key={v.id} className="bg-white p-5 rounded-xl border border-slate-200 shadow-2xs space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="font-bold text-xs text-blue-900">{v.variationNumber}</span>
                    <span
                      className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                        v.status === 'APPROVED'
                          ? 'bg-emerald-100 text-emerald-800'
                          : v.status === 'REJECTED'
                          ? 'bg-red-100 text-red-800'
                          : 'bg-amber-100 text-amber-800'
                      }`}
                    >
                      {v.status}
                    </span>
                  </div>

                  <div>
                    <h3 className="font-bold text-sm text-slate-900">{v.title}</h3>
                    <p className="text-xs text-slate-600 mt-1">{v.description}</p>
                  </div>

                  {/* Quantities display */}
                  <div className="grid grid-cols-3 gap-2 bg-slate-50 p-2.5 rounded-lg border border-slate-100 text-center text-xs">
                    <div>
                      <div className="text-[10px] text-slate-400 font-semibold uppercase">Original</div>
                      <div className="font-mono font-bold text-slate-700">{v.originalQuantity} {v.unit}</div>
                    </div>
                    <div>
                      <div className="text-[10px] text-amber-600 font-semibold uppercase">Additional</div>
                      <div className="font-mono font-bold text-amber-600">+{v.additionalQuantity} {v.unit}</div>
                    </div>
                    <div>
                      <div className="text-[10px] text-blue-600 font-semibold uppercase">Revised</div>
                      <div className="font-mono font-bold text-blue-700">{v.revisedQuantity} {v.unit}</div>
                    </div>
                  </div>

                  <div className="text-xs text-slate-500 space-y-1">
                    <div><span className="font-semibold text-slate-600">Reason:</span> {v.reason}</div>
                    {v.measurements && <div><span className="font-semibold text-slate-600">Measurements:</span> {v.measurements}</div>}
                    <div><span className="font-semibold text-slate-600">Requested By:</span> {v.requesterName} ({v.requesterRole})</div>
                  </div>

                  {/* Management Approval Action */}
                  {isSupervisorOrAdmin && v.status !== 'APPROVED' && (
                    <div className="pt-2 border-t border-slate-100 flex justify-end gap-2">
                      <button
                        onClick={() => handleApproveVariation(v.id)}
                        className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-xs font-bold transition-colors"
                      >
                        Approve Variation
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* MODAL: Log Milestone Progress */}
      {showProgressModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-xs">
          <div className="bg-white rounded-xl shadow-2xl border border-slate-200 max-w-lg w-full p-5 space-y-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between border-b border-slate-100 pb-3">
              <h3 className="font-bold text-slate-900 text-sm">Log Execution Progress</h3>
              <button onClick={() => setShowProgressModal(false)} className="text-slate-400 hover:text-slate-600">✕</button>
            </div>

            <form onSubmit={handleProgressSubmit} className="space-y-3 text-xs">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block font-semibold text-slate-700 mb-1">Progress Stage *</label>
                  <select
                    className="w-full border border-slate-200 rounded-lg p-2"
                    value={progressForm.stage}
                    onChange={(e) => {
                      const stg = e.target.value;
                      let pct = 50;
                      if (stg === '25%') pct = 25;
                      else if (stg === '50%') pct = 50;
                      else if (stg === '75%') pct = 75;
                      else if (stg === 'COMPLETED') pct = 100;
                      else if (stg === 'WORK_STARTED') pct = 10;
                      setProgressForm({ ...progressForm, stage: stg, percent: pct });
                    }}
                  >
                    <option value="WORK_STARTED">Work Started (10%)</option>
                    <option value="25%">25% Complete</option>
                    <option value="50%">50% Complete</option>
                    <option value="75%">75% Complete</option>
                    <option value="COMPLETED">100% Completed</option>
                  </select>
                </div>

                <div>
                  <label className="block font-semibold text-slate-700 mb-1">Progress %</label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    className="w-full border border-slate-200 rounded-lg p-2"
                    value={progressForm.percent}
                    onChange={(e) => setProgressForm({ ...progressForm, percent: Number(e.target.value) })}
                  />
                </div>
              </div>

              <div>
                <label className="block font-semibold text-slate-700 mb-1">Work Description *</label>
                <textarea
                  required
                  rows={2}
                  placeholder="Describe work completed at this milestone..."
                  className="w-full border border-slate-200 rounded-lg p-2"
                  value={progressForm.description}
                  onChange={(e) => setProgressForm({ ...progressForm, description: e.target.value })}
                />
              </div>

              <div>
                <label className="block font-semibold text-slate-700 mb-1">Material Used</label>
                <input
                  type="text"
                  placeholder="e.g. 4 sheets commercial ply 16mm used"
                  className="w-full border border-slate-200 rounded-lg p-2"
                  value={progressForm.materialUsage}
                  onChange={(e) => setProgressForm({ ...progressForm, materialUsage: e.target.value })}
                />
              </div>

              <div>
                <label className="block font-semibold text-slate-700 mb-1">Remaining Material</label>
                <input
                  type="text"
                  placeholder="e.g. 4 sheets commercial ply remaining for shutters"
                  className="w-full border border-slate-200 rounded-lg p-2"
                  value={progressForm.remainingMaterial}
                  onChange={(e) => setProgressForm({ ...progressForm, remainingMaterial: e.target.value })}
                />
              </div>

              <div>
                <label className="block font-semibold text-slate-700 mb-1">Photo Evidence URL</label>
                <input
                  type="url"
                  placeholder="https://images.unsplash.com/..."
                  className="w-full border border-slate-200 rounded-lg p-2"
                  value={progressForm.photoUrl}
                  onChange={(e) => setProgressForm({ ...progressForm, photoUrl: e.target.value })}
                />
              </div>

              <div className="flex justify-end gap-2 pt-3 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setShowProgressModal(false)}
                  className="px-3 py-2 border border-slate-200 rounded-lg text-slate-600 hover:bg-slate-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={progressSubmitting}
                  className="px-4 py-2 bg-[#0f2942] hover:bg-[#183d5f] text-white rounded-lg font-semibold"
                >
                  {progressSubmitting ? 'Submitting...' : 'Save Progress'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* MODAL: Submit Material Request */}
      {showReqModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-xs">
          <div className="bg-white rounded-xl shadow-2xl border border-slate-200 max-w-lg w-full p-5 space-y-4">
            <div className="flex items-center justify-between border-b border-slate-100 pb-3">
              <h3 className="font-bold text-slate-900 text-sm">New Material Requirement</h3>
              <button onClick={() => setShowReqModal(false)} className="text-slate-400 hover:text-slate-600">✕</button>
            </div>

            <form onSubmit={handleReqSubmit} className="space-y-3 text-xs">
              <div>
                <label className="block font-semibold text-slate-700 mb-1">Material Name *</label>
                <input
                  required
                  type="text"
                  className="w-full border border-slate-200 rounded-lg p-2"
                  value={reqForm.materialName}
                  onChange={(e) => setReqForm({ ...reqForm, materialName: e.target.value })}
                />
              </div>

              <div className="grid grid-cols-3 gap-2">
                <div>
                  <label className="block font-semibold text-slate-700 mb-1">Calc Qty *</label>
                  <input
                    required
                    type="number"
                    step="0.01"
                    className="w-full border border-slate-200 rounded-lg p-2"
                    value={reqForm.calculatedQuantity}
                    onChange={(e) => {
                      const calc = Number(e.target.value);
                      const waste = Number(reqForm.wastageQuantity);
                      setReqForm({
                        ...reqForm,
                        calculatedQuantity: e.target.value,
                        requestedQuantity: String(calc + waste),
                      });
                    }}
                  />
                </div>
                <div>
                  <label className="block font-semibold text-slate-700 mb-1">Wastage Qty *</label>
                  <input
                    required
                    type="number"
                    step="0.01"
                    className="w-full border border-slate-200 rounded-lg p-2"
                    value={reqForm.wastageQuantity}
                    onChange={(e) => {
                      const waste = Number(e.target.value);
                      const calc = Number(reqForm.calculatedQuantity);
                      setReqForm({
                        ...reqForm,
                        wastageQuantity: e.target.value,
                        requestedQuantity: String(calc + waste),
                      });
                    }}
                  />
                </div>
                <div>
                  <label className="block font-semibold text-slate-700 mb-1">Requested Qty *</label>
                  <input
                    required
                    type="number"
                    step="0.01"
                    className="w-full border border-slate-200 rounded-lg p-2"
                    value={reqForm.requestedQuantity}
                    onChange={(e) => setReqForm({ ...reqForm, requestedQuantity: e.target.value })}
                  />
                </div>
              </div>

              <div>
                <label className="block font-semibold text-slate-700 mb-1">Notes</label>
                <input
                  type="text"
                  placeholder="Justification for order quantities..."
                  className="w-full border border-slate-200 rounded-lg p-2"
                  value={reqForm.notes}
                  onChange={(e) => setReqForm({ ...reqForm, notes: e.target.value })}
                />
              </div>

              <div className="flex justify-end gap-2 pt-3 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setShowReqModal(false)}
                  className="px-3 py-2 border border-slate-200 rounded-lg text-slate-600 hover:bg-slate-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold"
                >
                  Save Requirement
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* MODAL: Approve Extra Material Requirement */}
      {showApproveReqModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-xs">
          <div className="bg-white rounded-xl shadow-2xl border border-slate-200 max-w-sm w-full p-5 space-y-4">
            <h3 className="font-bold text-slate-900 text-sm">Approve Extra Quantity</h3>
            <p className="text-xs text-slate-500">
              Required: {showApproveReqModal.requiredQuantity} | Requested: {showApproveReqModal.requestedQuantity} (Variance: +{showApproveReqModal.varianceQuantity})
            </p>

            <div>
              <label className="block font-semibold text-slate-700 text-xs mb-1">Approved Quantity:</label>
              <input
                type="number"
                step="0.01"
                className="w-full border border-slate-200 rounded-lg p-2 text-xs"
                value={approveQtyInput}
                onChange={(e) => setApproveQtyInput(e.target.value)}
              />
            </div>

            <div className="flex justify-end gap-2 pt-2 border-t border-slate-100 text-xs">
              <button
                type="button"
                onClick={() => setShowApproveReqModal(null)}
                className="px-3 py-1.5 border border-slate-200 rounded-lg text-slate-600"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => handleApproveReq(showApproveReqModal.id)}
                className="px-4 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-bold"
              >
                Confirm Approval
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL: Log Material Usage On Site */}
      {showUsageModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-xs">
          <div className="bg-white rounded-xl shadow-2xl border border-slate-200 max-w-sm w-full p-5 space-y-4">
            <h3 className="font-bold text-slate-900 text-sm">Log Material Usage</h3>
            <p className="text-xs text-slate-500">
              {showUsageModal.materialName || showUsageModal.materialCode} ({showUsageModal.unit})
            </p>
            <div className="p-2.5 bg-slate-50 rounded-lg text-xs space-y-1">
              <div><span className="text-slate-500">Fulfilled Qty:</span> <strong className="font-mono">{showUsageModal.fulfilledQuantity || '0.00'}</strong></div>
              <div><span className="text-slate-500">Current Used:</span> <span className="font-mono">{showUsageModal.usedQuantity || '0.00'}</span></div>
              <div><span className="text-slate-500">Current Remaining:</span> <span className="font-mono text-emerald-700 font-bold">{showUsageModal.remainingQuantity || '0.00'}</span></div>
            </div>

            <form onSubmit={handleUsageSubmit} className="space-y-3 text-xs">
              <div>
                <label className="block font-semibold text-slate-700 mb-1">New Total Used Quantity ({showUsageModal.unit}) *</label>
                <input
                  type="number"
                  step="0.01"
                  required
                  className="w-full border border-slate-200 rounded-lg p-2"
                  value={usageQtyInput}
                  onChange={(e) => setUsageQtyInput(e.target.value)}
                />
              </div>

              <div>
                <label className="block font-semibold text-slate-700 mb-1">Usage Notes / Installation Area</label>
                <input
                  type="text"
                  placeholder="e.g. Installed in main banking hall"
                  className="w-full border border-slate-200 rounded-lg p-2"
                  value={usageNotesInput}
                  onChange={(e) => setUsageNotesInput(e.target.value)}
                />
              </div>

              <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setShowUsageModal(null)}
                  className="px-3 py-1.5 border border-slate-200 rounded-lg text-slate-600"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-bold"
                >
                  Update Usage
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* MODAL: Submit Contractor Bill */}
      {showBillModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-xs">
          <div className="bg-white rounded-xl shadow-2xl border border-slate-200 max-w-lg w-full p-5 space-y-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between border-b border-slate-100 pb-3">
              <h3 className="font-bold text-slate-900 text-sm">Submit Contractor Purchase Bill</h3>
              <button onClick={() => setShowBillModal(false)} className="text-slate-400 hover:text-slate-600">✕</button>
            </div>

            <form onSubmit={handleBillSubmit} className="space-y-3 text-xs">
              <div>
                <label className="block font-semibold text-slate-700 mb-1">Contractor Name *</label>
                <input
                  required
                  type="text"
                  className="w-full border border-slate-200 rounded-lg p-2"
                  value={billForm.contractorName}
                  onChange={(e) => setBillForm({ ...billForm, contractorName: e.target.value })}
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block font-semibold text-slate-700 mb-1">Quantity Purchased *</label>
                  <input
                    required
                    type="number"
                    step="0.01"
                    className="w-full border border-slate-200 rounded-lg p-2"
                    value={billForm.quantity}
                    onChange={(e) => setBillForm({ ...billForm, quantity: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block font-semibold text-slate-700 mb-1">Total Amount Paid (PKR) *</label>
                  <input
                    required
                    type="number"
                    step="0.01"
                    className="w-full border border-slate-200 rounded-lg p-2 font-mono"
                    value={billForm.amountPaid}
                    onChange={(e) => setBillForm({ ...billForm, amountPaid: e.target.value })}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block font-semibold text-slate-700 mb-1">Supplier Market / Shop</label>
                  <input
                    type="text"
                    className="w-full border border-slate-200 rounded-lg p-2"
                    value={billForm.supplierName}
                    onChange={(e) => setBillForm({ ...billForm, supplierName: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block font-semibold text-slate-700 mb-1">Bill / Invoice Number</label>
                  <input
                    type="text"
                    className="w-full border border-slate-200 rounded-lg p-2"
                    value={billForm.invoiceOrBillNumber}
                    onChange={(e) => setBillForm({ ...billForm, invoiceOrBillNumber: e.target.value })}
                  />
                </div>
              </div>

              <div>
                <label className="block font-semibold text-slate-700 mb-1">Receipt Photo URL *</label>
                <input
                  required
                  type="url"
                  className="w-full border border-slate-200 rounded-lg p-2"
                  value={billForm.receiptPhotoUrl}
                  onChange={(e) => setBillForm({ ...billForm, receiptPhotoUrl: e.target.value })}
                />
              </div>

              <div className="flex justify-end gap-2 pt-3 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setShowBillModal(false)}
                  className="px-3 py-2 border border-slate-200 rounded-lg text-slate-600 hover:bg-slate-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-semibold"
                >
                  Upload Bill
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* MODAL: Submit Work Variation */}
      {showVariationModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-xs">
          <div className="bg-white rounded-xl shadow-2xl border border-slate-200 max-w-lg w-full p-5 space-y-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between border-b border-slate-100 pb-3">
              <h3 className="font-bold text-slate-900 text-sm">Submit Work Variation Request</h3>
              <button onClick={() => setShowVariationModal(false)} className="text-slate-400 hover:text-slate-600">✕</button>
            </div>

            <form onSubmit={handleVariationSubmit} className="space-y-3 text-xs">
              <div>
                <label className="block font-semibold text-slate-700 mb-1">Variation Title *</label>
                <input
                  required
                  type="text"
                  placeholder="e.g. Additional Backing Sub-frame for Cash Counter"
                  className="w-full border border-slate-200 rounded-lg p-2"
                  value={variationForm.title}
                  onChange={(e) => setVariationForm({ ...variationForm, title: e.target.value })}
                />
              </div>

              <div>
                <label className="block font-semibold text-slate-700 mb-1">Variation Type</label>
                <select
                  className="w-full border border-slate-200 rounded-lg p-2"
                  value={variationForm.variationType}
                  onChange={(e) => setVariationForm({ ...variationForm, variationType: e.target.value as any })}
                >
                  <option value="ADDITIONAL_MATERIAL">Additional Material</option>
                  <option value="ADDITIONAL_WORK">Additional Work Scope</option>
                  <option value="SCOPE_CHANGE">Scope Modification</option>
                </select>
              </div>

              <div className="grid grid-cols-3 gap-2">
                <div>
                  <label className="block font-semibold text-slate-700 mb-1">Original Qty *</label>
                  <input
                    required
                    type="number"
                    step="0.01"
                    className="w-full border border-slate-200 rounded-lg p-2"
                    value={variationForm.originalQuantity}
                    onChange={(e) => setVariationForm({ ...variationForm, originalQuantity: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block font-semibold text-slate-700 mb-1">Additional Qty *</label>
                  <input
                    required
                    type="number"
                    step="0.01"
                    className="w-full border border-slate-200 rounded-lg p-2"
                    value={variationForm.additionalQuantity}
                    onChange={(e) => setVariationForm({ ...variationForm, additionalQuantity: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block font-semibold text-slate-700 mb-1">Unit</label>
                  <input
                    type="text"
                    className="w-full border border-slate-200 rounded-lg p-2"
                    value={variationForm.unit}
                    onChange={(e) => setVariationForm({ ...variationForm, unit: e.target.value })}
                  />
                </div>
              </div>

              <div>
                <label className="block font-semibold text-slate-700 mb-1">Reason for Variation *</label>
                <textarea
                  required
                  rows={2}
                  placeholder="Explain why extra materials or work is required..."
                  className="w-full border border-slate-200 rounded-lg p-2"
                  value={variationForm.reason}
                  onChange={(e) => setVariationForm({ ...variationForm, reason: e.target.value })}
                />
              </div>

              <div>
                <label className="block font-semibold text-slate-700 mb-1">Site Measurements / Dimensions</label>
                <input
                  type="text"
                  placeholder="e.g. 8 ft x 4 ft wall backing area"
                  className="w-full border border-slate-200 rounded-lg p-2"
                  value={variationForm.measurements}
                  onChange={(e) => setVariationForm({ ...variationForm, measurements: e.target.value })}
                />
              </div>

              <div>
                <label className="block font-semibold text-slate-700 mb-1">Photo Evidence URL</label>
                <input
                  type="url"
                  placeholder="https://images.unsplash.com/..."
                  className="w-full border border-slate-200 rounded-lg p-2"
                  value={variationForm.photoUrl}
                  onChange={(e) => setVariationForm({ ...variationForm, photoUrl: e.target.value })}
                />
              </div>

              <div className="flex justify-end gap-2 pt-3 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setShowVariationModal(false)}
                  className="px-3 py-2 border border-slate-200 rounded-lg text-slate-600 hover:bg-slate-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={variationSubmitting}
                  className="px-4 py-2 bg-[#0f2942] hover:bg-[#183d5f] text-white rounded-lg font-semibold"
                >
                  {variationSubmitting ? 'Submitting...' : 'Submit Variation'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
